import { animate, state, style, transition, trigger } from '@angular/animations';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChange,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  addDays,
  addMonths,
  addWeeks,
  differenceInDays,
  differenceInMonths,
  differenceInSeconds,
  differenceInWeeks,
  endOfDay,
  endOfMonth,
  endOfWeek,
  format,
  isWeekend,
  parseISO,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import isEqual from 'fast-deep-equal';
import max from 'lodash-es/max';
import min from 'lodash-es/min';
import {
  BehaviorSubject,
  combineLatest,
  delay,
  delayWhen,
  distinctUntilChanged,
  filter,
  map,
  merge,
  Observable,
  of,
  scan,
  shareReplay,
  Subject,
  tap,
} from 'rxjs';

import { makeStylesForTag, TagConfig, UpdatePropRequest } from '@blaze-runtime/engine-domain';
import {
  ContainerElementMetaUiFeatures,
  DataBindingSourceInfo,
  formatBorderRadiusCorners,
  GanttChartCustomField,
  GanttChartElementWithValue,
  getColumnFormatOptions,
  getFormatForField,
  ITEMS_CONTAINER_ACTION_ADD,
  ITEMS_CONTAINER_ACTION_DELETE,
  ITEMS_CONTAINER_ACTION_EDIT,
  ITEMS_CONTAINER_ACTION_ROW_CLICK,
  ITEMS_CONTAINER_ACTION_VIEW,
  makeBorderStyles,
  makeContainerVisualEffectStyles,
  PropertiesConfigInputColorPaletteModel,
  PropertiesConfigInputContainerItemActionModel,
  PropertiesConfigInputListCustomFieldModel,
  RowClickAction,
  TableColumnFormat,
  WithRowActions,
} from '@blaze-shared/elements-domain';
import { sortByKey } from '@blaze-shared/platform-util';
import {
  BlazeSchema,
  findColumn,
  getDatasetLinkDisplay,
  isDatasetLinkValue,
} from '@blaze-shared/schema-domain';
import { AnyFormatValueOptions } from '@blaze-shared/shared-feature';
import {
  groupByMap,
  isDefined,
  isElementOrChildOf,
  setAlphaToHexColor,
  trackByField,
  trackByIndex,
  useEmitTracker,
} from '@blaze-shared/shared-util';
import { FALLBACK_THEME, StyleSheetTarget } from '@blaze-shared/theming-domain';

import { CanvasElementComponent } from '../../canvas-elements/element-component';
import { arePropsWithData } from '../../canvas-elements/element-model/element-props-guards';
import { ContainerActionsService } from '../../services/container-actions.service';
import { CanvasElementBaseComponent } from '../__base/base.component';

import { GanttChartPropsModel, GanttChartTimeScale } from './gantt-chart.models';

type GanttSubcolumn = {
  label: string;
  weekend: boolean;
};
type GanttModel = {
  columns: {
    label: string;
    days: GanttSubcolumn[];
  }[];
  withCategories: boolean;
  categories: GanttCategory[];
  now?: GanttMarker;
  collapsedCategories: Set<string | undefined>;
  selectedId?: string;
};
type GanttCategory = GanttRow & {
  type: 'category';
  id?: string;
  label: string;
  entries: GanttEntry[];
};
type GanttEntry = GanttRow & {
  type: 'entry';
  id?: string;
  task?: string;
  custom$?: Observable<GanttChartCustomField[]>;
};
type GanttRow = {
  type: string;
  column?: number;
  start?: number;
  length?: number;
  buckets?: number;
  tooltip?: string;
  cells: void[];
};
type GanttMarker = {
  column: number;
  pos: number;
};

const PLACEHOLDER_DATA: GanttChartElementWithValue[] = [
  {
    task: 'task',
    startDate: '2023-01-01 00:00:00',
    endDate: '2023-02-16 12:00:00',
    custom$: of([]),
  },
  {
    task: 'task',
    startDate: '2023-02-01 00:00:00',
    endDate: '2023-03-31 23:59:59',
    custom$: of([]),
  },
  {
    task: 'task',
    startDate: '2023-03-14 12:00:00',
    endDate: '2023-04-15 12:00:00',
    custom$: of([]),
  },
  {
    task: 'task',
    startDate: '2023-04-10 12:00:00',
    endDate: '2023-04-30 23:59:59',
    custom$: of([]),
  },
];

const PLACEHOLDER_NOW_MARKER: GanttMarker = {
  column: 2,
  pos: 0,
};

@UntilDestroy()
@Component({
  selector: 'be-gantt-chart',
  animations: [
    trigger('collapsed', [
      state('true', style({ height: 0 })),
      state('false', style({ height: '*' })),
      transition('* => *', [animate('0.5s ease')]),
    ]),
  ],
  templateUrl: './gantt-chart.component.html',
  styleUrls: ['./gantt-chart.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class GanttChartComponent
  extends CanvasElementBaseComponent<'ganttChart'>
  implements CanvasElementComponent<'ganttChart'>, OnChanges, OnInit, OnDestroy
{
  private readonly containerActionsService = inject(ContainerActionsService);

  @Input() value!: any[];

  @Output() updateProp = new EventEmitter<UpdatePropRequest>();

  @ViewChild('now') nowStrip?: ElementRef<HTMLElement>;
  @ViewChild('headingTask') headingTask?: ElementRef<HTMLElement>;
  @ViewChild('scroll') scrollContainer?: ElementRef<HTMLElement>;

  @HostBinding('@.disabled') animationsDisabled = false;
  @HostBinding('style.--b-gc-border-width') borderWidth: string;
  @HostBinding('style.--b-gc-cell-width') cellWidth?: string;
  @HostBinding('style.--b-gc-header-height') headerHeight?: string;
  @HostBinding('style.--b-gc-header-width') headerWidth?: string;
  @HostBinding('style.--b-gc-scroll-height') scrollHeight?: string;
  @HostBinding('style.--b-gc-font-size') fontSize = FALLBACK_THEME.value.fontSize;
  @HostBinding('style.--b-gc-font-family') fontFamily = FALLBACK_THEME.value.fontFamily;
  @HostBinding('style.--b-gc-line-height') lineHeight = FALLBACK_THEME.value.lineHeight;
  @HostBinding('style.--b-gc-title-color') titleColor = '#000';
  @HostBinding('style.--b-gc-header-color') headerColor = '#000';
  @HostBinding('style.--b-gc-header-bg') headerBackground = '#fff';
  @HostBinding('style.--b-gc-accent-color') accentColor = '#00f';
  @HostBinding('style.--b-gc-hover-color') hoverColor = '#00f1';
  @HostBinding('style.--b-gc-select-color') selectColor = '#00f2';
  @HostBinding('style.--b-gc-cell-color') cellColor = '#000';
  @HostBinding('style.--b-gc-cell-bg') cellBackground = '#fff';
  @HostBinding('style.--b-gc-border-color') borderColor = 'transparent';
  @HostBinding('style.--b-gc-border-radius') borderRadius = '0';
  @HostBinding('style.--b-gc-border-radius-bl') borderRadiusBl = '0';
  @HostBinding('style.--b-gc-border-radius-br') borderRadiusBr = '0';
  @HostBinding('style.--b-gc-internal-padding') internalPadding? = '0';

  private readonly outputEmitTracker = useEmitTracker();
  private readonly dataValue = this.getDataInput('value');
  private readonly schema$ = this.dataValue.schema$;

  private readonly triggerUpdate$ = new Subject<typeof this.value>();
  private readonly customFields$$ = new BehaviorSubject<
    PropertiesConfigInputListCustomFieldModel[]
  >([]);

  private categoryPalette$!: Observable<PropertiesConfigInputColorPaletteModel | undefined>;
  private dataSource$$ = new BehaviorSubject<DataBindingSourceInfo | undefined>(undefined);

  borderless = false;
  borderWidthPx = 1;

  trackByIndex = trackByIndex;
  trackByTask = trackByField<GanttEntry>('task');
  trackByCategory = trackByField<GanttCategory>('label');

  dataSource = new MatTableDataSource<GanttChartElementWithValue>([]);
  model$$: BehaviorSubject<GanttModel> = new BehaviorSubject<GanttModel>({
    columns: [],
    withCategories: false,
    categories: [],
    collapsedCategories: new Set(),
  });
  hasDays = false;

  isLoading$: Observable<boolean> = merge(
    this.isPending$.pipe(map((pending) => ({ pending }))),
    this.model$$.pipe(map(() => ({ data: true }))),
  ).pipe(
    scan(
      (state, el) => {
        if ('pending' in el) {
          return { ...el, data: el.pending ? false : state.data };
        }
        if ('data' in el && !state.pending) {
          return { pending: false, ...el };
        }
        return state;
      },
      { pending: false, data: false },
    ),
    map(({ pending, data }) => pending || !data),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  itemViewEnabled = false;
  itemViewStyleSheetTargetOrId?: StyleSheetTarget | string;
  itemAddEnabled = false;
  itemAddLabel = '';
  itemAddStyleSheetTargetOrId?: StyleSheetTarget | string;
  itemEditEnabled = false;
  itemEditStyleSheetTargetOrId?: StyleSheetTarget | string;

  labelEnabled = false;
  labelText = '';
  icon = '';
  wrapperStyles: Record<string, string> | null = null;

  categoryStyles: Record<string, Record<string, string | undefined>> = {};

  categoryFieldFormat: TableColumnFormat = 'text';
  categoryFieldFormatOptions: AnyFormatValueOptions | undefined;

  taskFieldFormat: TableColumnFormat = 'text';
  taskFieldFormatOptions: AnyFormatValueOptions | undefined;
  tagConfig: TagConfig = [];

  readonly elementActionsKeyRoot = 'b-gcac';
  elementActionsKey!: string;

  get hasBoundValue(): boolean {
    return !!this.bindingResults?.['value'];
  }

  constructor() {
    super();
    this.borderWidth = `${this.borderWidthPx}px`;
    this.initMarginHandler((props) => props?.style?.general?.offset?.margin);
  }

  ngOnChanges(changes: SimpleChanges): void {
    /**
     * Declare output schemas when no value is bound to element.
     */
    if (!this.bindingResults?.['value']) {
      this.setDefaultEventSchema('row-select');
      this.setDefaultEventSchema('action');
    }

    if (changes?.['elementId'] && !this.elementActionsKey) {
      this.elementActionsKey = `${this.elementActionsKeyRoot}-${this.elementId}`;
    }

    if (changes.value || changes.props) {
      if (changes.value || this.hasFieldsChanged(changes.props)) {
        this.triggerUpdate$.next(this.value);
      }
      this.updateStyles();
    }

    if (changes.props) {
      const { content } = this.props || {};

      this.itemViewEnabled = content?.itemView?.enabled || false;
      this.itemViewStyleSheetTargetOrId =
        content?.itemView?.styleSheetTarget ?? content?.itemView?.styleSheetId;
      this.itemAddEnabled = content?.itemAdd?.enabled || false;
      this.itemAddLabel = content?.itemAdd?.label || '';
      this.itemAddStyleSheetTargetOrId =
        content?.itemAdd?.styleSheetTarget ?? content?.itemAdd?.styleSheetId;
      this.itemEditEnabled = content?.itemEdit?.enabled || false;
      this.itemEditStyleSheetTargetOrId =
        content?.itemEdit?.styleSheetTarget ?? content?.itemEdit?.styleSheetId;
      this.dataSource$$.next(this.getDataSource());
      this.taskFieldFormat = getFormatForField(
        this.props?.data,
        'value',
        this.props?.content.fields?.task,
        'text',
      );
      this.taskFieldFormatOptions = getColumnFormatOptions(
        this.props?.data,
        'value',
        this.props?.content.fields?.task,
      );
      this.categoryFieldFormat = getFormatForField(
        this.props?.data,
        'value',
        this.props?.content.fields?.category,
        'text',
      );
      this.categoryFieldFormatOptions = getColumnFormatOptions(
        this.props?.data,
        'value',
        this.props?.content.fields?.category,
      );
    }
  }

  ngOnInit(): void {
    /**
     * Declare output schemas when bindings change.
     */
    this.schema$.pipe(filter(Boolean), untilDestroyed(this)).subscribe((schema: BlazeSchema) => {
      this.setDefaultEventSchema(
        'row-select',
        this.createElementSchema('row-select', undefined, schema),
      );
      this.setDefaultEventSchema('action', this.createElementSchema('action', undefined, schema));
    });

    this.triggerUpdate$
      .pipe(
        delayWhen(() => this.getElementEventSchemaSet('row-select')),
        untilDestroyed(this),
      )
      .subscribe((value) => {
        this.updateValue(value);
      });

    if (!this.bindingResults?.['value']) {
      this.updateDataSet(undefined);
    }

    combineLatest({
      data: this.dataSource.connect(),
      timeScale: this.props$.pipe(
        map((props) => [props?.content.options.timeScale, props?.content.options.startWeekOn]),
        distinctUntilChanged(isEqual),
      ),
    })
      .pipe(
        untilDestroyed(this),
        map(({ data }) => this.mapData(data)),
        tap(() => {
          this.hasDays = this.props?.content.options.timeScale !== GanttChartTimeScale.Month;
          this.headerHeight = this.hasDays ? '5rem' : '3.5rem';
          const CELL_WIDTH: Record<GanttChartTimeScale, string> = {
            [GanttChartTimeScale.Month]: '144px',
            [GanttChartTimeScale.Week]: '204px',
            [GanttChartTimeScale.Day]: '72px',
          };
          this.cellWidth = this.props && CELL_WIDTH[this.props.content.options.timeScale];
          this.animationsDisabled = true;
        }),
        delay(0),
        tap(() => {
          setTimeout(() => {
            // after view is rendered
            this.updateCollapse();
            this.animationsDisabled = false;
            this.scrollToNow(true);
            this.cdr.detectChanges();
          });
        }),
      )
      .subscribe(this.model$$);

    this.categoryPalette$ = this.props$.pipe(
      map((props) => props?.style.bar?.palette),
      distinctUntilChanged(isEqual),
    );

    // Create styles for category bars
    combineLatest({
      paletteModel: this.categoryPalette$,
      categoryIds: this.model$$.pipe(
        map(({ categories }) => categories.map(({ id }) => id ?? '')),
        distinctUntilChanged((a, b) => isEqual(a, b)),
      ),
    })
      .pipe(
        map(({ paletteModel, categoryIds }) => {
          const palette = this.elementTagsService.mapValuesToColorPalette(
            categoryIds,
            undefined,
            undefined,
            paletteModel,
          );
          return Object.fromEntries(categoryIds.map((id) => [id, makeStylesForTag(id, palette)]));
        }),
        untilDestroyed(this),
      )
      .subscribe((categoryStyles) => {
        this.categoryStyles = categoryStyles;
      });

    this.createTagConfig(
      this.props$.pipe(map((props) => props?.content.customFields)),
      this.props$.pipe(map((props) => props?.content.columns)),
      this.createPaletteSchemaObservable(this.dataSource$$),
      this.dataValue.data$,
    ).subscribe((tagConfig) => {
      this.tagConfig = tagConfig;
      this.cdr.detectChanges();
    });

    this.initActionStyles(
      this.elementActionsKey,
      this.props$.pipe(
        map((props) =>
          props?.content ? getRowActions((props as GanttChartPropsModel).content, true) : [],
        ),
      ),
      ['efh-action-btn', 'action-btn'],
    );

    this.getElementUiFeatures<ContainerElementMetaUiFeatures>()
      .pipe(
        untilDestroyed(this),
        map(({ columns }) => {
          return (this.props?.content?.customFields || []).map((field) => {
            const colProps = findColumn(columns, field.column);
            if (!colProps) {
              return field;
            }
            return {
              ...field,
              label: colProps.name || field.label,
              displayFormat: colProps.format || field.displayFormat,
              precision: colProps.precision || field.precision,
              currency: colProps.currency || field.currency,
              duration: colProps.duration || field.duration,
              dateFormat: colProps.dateFormat || field.dateFormat,
              dateTimeFormat: colProps.dateTimeFormat || field.dateTimeFormat,
            };
          });
        }),
      )
      .subscribe(this.customFields$$);
  }

  ngOnDestroy(): void {
    this.dataSource.disconnect();
  }

  onAction(
    event: Event,
    action: PropertiesConfigInputContainerItemActionModel,
    entry: GanttEntry,
  ): void {
    const dataSource = this.getDataSource();
    if (!dataSource) {
      return console.error(`Action "${action.label}" failed. Data source not configured.`);
    }

    const idColumn = this.getIdColumnName(dataSource);
    const dataRow = idColumn && this.value.find(({ [idColumn]: id }) => id === entry.id);
    const dataRowSchema = this.createElementSchema('action', undefined, this.dataValue.schema);

    this.containerActionsService.handleListlikeAction({
      dataSource,
      action,
      props: this.props as GanttChartPropsModel,
      output: this.output,
      dataRow,
      dataRowSchema,
      dataRowId: entry.id,
    });
  }

  onEntryClick(event: Event, entry: GanttEntry): void {
    if (isElementOrChildOf(event.target as HTMLElement, (e) => e.tagName === 'BUTTON')) {
      return;
    }
    this.model$$.next({ ...this.model$$.value, selectedId: entry.id });

    const idColumn = this.getIdColumnName(this.getDataSource());
    const dataRow = idColumn && this.value.find(({ [idColumn]: id }) => id === entry.id);
    this.updateOutput(dataRow);
    if (this.itemViewEnabled) {
      this.onViewRow(event, entry);
    }
  }

  onAddNewRow(event: Event): void {
    if (!this.itemAddEnabled) {
      return;
    }
    this.onAction(
      event,
      { id: ITEMS_CONTAINER_ACTION_ADD, label: this.itemAddLabel },
      {} as GanttEntry,
    );
  }

  onViewRow(event: Event, entry: GanttEntry): void {
    if (!this.itemViewEnabled) {
      return;
    }
    this.onAction(event, { id: ITEMS_CONTAINER_ACTION_VIEW, label: 'View' }, entry);
  }

  onEditRow(event: Event, entry: GanttEntry): void {
    if (!this.itemEditEnabled) {
      return;
    }
    this.onAction(event, { id: ITEMS_CONTAINER_ACTION_EDIT, label: 'Edit' }, entry);
  }

  percent(value: number): string {
    return `${Math.round(value * 100)}%`;
  }

  showToday(): void {
    this.scrollToNow(false);
  }

  collapseCategory(id: string | undefined, collapse?: boolean): void {
    if (collapse ?? !this.model$$.value.collapsedCategories.has(id)) {
      this.model$$.value.collapsedCategories.add(id);
    } else {
      this.model$$.value.collapsedCategories.delete(id);
    }
  }

  collapseAll(): void {
    const collapse =
      this.model$$.value.collapsedCategories.size < this.model$$.value.categories.length;
    this.model$$.value.categories.forEach(({ id }) => {
      this.collapseCategory(id, collapse);
    });
  }

  /**
   * Checks if a column is a secure column
   * @param field custom field definition
   */
  isFieldSecure(field: GanttChartCustomField): boolean {
    return findColumn(this.dataValue.schema?.columns, field.column)?.isSecure ?? false;
  }

  onResizeColumn(width: number): void {
    this.updateProp.emit({
      path: 'style.headers.width',
      payload: `${width}px`,
    });
  }

  onCustomFieldClick(field: GanttChartCustomField): void {
    if (field.displayFormat === 'url') {
      this.dataTypeActions.openPage(field.value);
    } else if (field.displayFormat === 'email') {
      this.dataTypeActions.copy(field.value);
    }
  }

  private scrollToNow(instant: boolean): void {
    const column = this.nowStrip?.nativeElement?.parentElement;
    const headingTask = this.headingTask?.nativeElement;
    const scrollContainer = this.scrollContainer?.nativeElement;
    if (!column || !headingTask || !scrollContainer) {
      return;
    }
    scrollContainer.scroll({
      left: column.offsetLeft - headingTask.clientWidth - 2 * this.borderWidthPx,
      behavior: instant ? ('instant' as any) : 'smooth',
    });
  }

  private hasFieldsChanged(change: SimpleChange | undefined): boolean {
    const currentValue = change?.currentValue as GanttChartPropsModel | undefined;
    const prevValue = change?.previousValue as GanttChartPropsModel | undefined;

    const keys: Array<keyof GanttChartPropsModel['content']> = [
      'columns',
      'fields',
      'customFields',
    ];
    return keys.some((key) => !isEqual(currentValue?.content?.[key], prevValue?.content?.[key]));
  }

  private updateValue(value?: any[]): void {
    this.updateDataSet(value);
    this.updateOutput(value?.[0]);
  }

  private updateDataSet(value?: any[]): void {
    const { content } = this.props ?? {};
    const { fields } = content ?? {};
    const category = this.getColumnName(fields?.category);
    const task = this.getColumnName(fields?.task);
    const startDate = this.getColumnName(fields?.startDate);
    const endDate = this.getColumnName(fields?.endDate);
    const idColumn = this.getIdColumnName(this.getDataSource());
    const customFields = this.customFields$$.value;

    const data = this.hasBoundValue
      ? value?.map(
          (row: any): GanttChartElementWithValue => ({
            id: idColumn && row[idColumn],
            category: category && row[category],
            task: task && row[task],
            startDate: startDate && row[startDate],
            endDate: endDate && row[endDate],
            custom$: this.runtimeContext$.pipe(
              map((context) => {
                const rowCustomFields = this.createCustomFields(row, customFields);

                if (!rowCustomFields) {
                  return [];
                }

                // If no custom fields are conditionally visible - show the same custom fields for all rows
                if (!customFields?.some((field) => !!field.visibilityExpression)) {
                  return rowCustomFields;
                }

                // Normalization is done here for improved performance - the normalized context is shared across all rows
                const normalizedContext = this.normalizeLinkedParams(context);

                // Add normalized row data to the context
                const rowContext = {
                  ...normalizedContext,
                  output: this.normalizeLinkedParams(row),
                };
                // Show row custom fields if the visibility expression is not provided or if it resolves to true
                return this.createCustomFields(
                  row,
                  customFields.filter(
                    (field) =>
                      !field.visibilityExpression ||
                      !!this.evaluateExpression(field.visibilityExpression, rowContext, true),
                  ),
                );
              }),
            ),
          }),
        ) || []
      : PLACEHOLDER_DATA;
    this.dataSource.data = data;
  }

  private createCustomFields(
    row: any,
    fieldsDef: PropertiesConfigInputListCustomFieldModel[] | undefined,
  ): GanttChartCustomField[] {
    if (!fieldsDef) {
      return [];
    }

    const customFields = fieldsDef.map((f) => {
      const columnName = this.getColumnName(f.column);
      const format = this.getFieldFormat(f.column);
      const value = (columnName && row[columnName]) || '';

      return { ...f, format, value };
    });

    return customFields;
  }

  private getFieldFormat(columnIdOrName?: string): TableColumnFormat {
    const column = findColumn(this.props?.content.columns, columnIdOrName);
    return column?.format || 'text';
  }

  private mapData(data: GanttChartElementWithValue[]): GanttModel {
    if (!this.props) {
      throw new Error('props not defined');
    }

    const dataValid = data.map((entry) => {
      const startDateParsed = entry.startDate == undefined ? undefined : parseISO(entry.startDate);
      const endDateParsed = entry.endDate == undefined ? undefined : parseISO(entry.endDate);
      const startDate =
        startDateParsed &&
        endDateParsed &&
        !isNaN(startDateParsed.valueOf()) &&
        startDateParsed <= endDateParsed
          ? startDateParsed
          : undefined;
      const endDate =
        startDateParsed &&
        endDateParsed &&
        !isNaN(endDateParsed.valueOf()) &&
        startDateParsed <= endDateParsed
          ? endDateParsed
          : undefined;
      return { ...entry, startDate, endDate };
    });

    type DataValid = { startDate: Date | undefined; endDate: Date | undefined };
    type Bucket = {
      startBucket: Date | undefined;
      endBucket: Date | undefined;
      startPosition: number | undefined;
      endPosition: number | undefined;
      buckets: number | undefined;
      length: number | undefined;
    };
    type BucketOperations = {
      start: (date: Date) => Date;
      end: (date: Date) => Date;
      difference: (dateEnd: Date, dateStart: Date) => number;
      next: (date: Date) => Date;
      title: (date: Date) => string;
      dayTitle: (date: Date) => string;
    };

    const weekStartsOn = this.props.content.options.startWeekOn;
    const BUCKET_OPERATIONS: Record<GanttChartTimeScale, BucketOperations> = {
      [GanttChartTimeScale.Month]: {
        start: startOfMonth,
        end: endOfMonth,
        difference: differenceInMonths,
        next: (bucket: Date) => addMonths(bucket, 1),
        title: (bucket: Date) => format(bucket, "MMM ''yy"),
        dayTitle: () => '',
      },
      [GanttChartTimeScale.Week]: {
        start: (date: Date) => startOfWeek(date, { weekStartsOn }),
        end: (date: Date) => endOfWeek(date, { weekStartsOn }),
        difference: (dateEnd: Date, dateStart: Date) => differenceInWeeks(dateEnd, dateStart),
        next: (bucket: Date) => addWeeks(bucket, 1),
        title: (bucket: Date) => {
          const startMonth = format(bucket, 'MMM');
          const endMonth = format(endOfWeek(bucket, { weekStartsOn }), 'MMM');
          return startMonth === endMonth ? startMonth : `${startMonth} - ${endMonth}`;
        },
        dayTitle: (bucket: Date) => format(bucket, 'd'),
      },
      [GanttChartTimeScale.Day]: {
        start: startOfDay,
        end: endOfDay,
        difference: differenceInDays,
        next: (bucket: Date) => addDays(bucket, 1),
        title: (bucket: Date) => format(bucket, 'MMM'),
        dayTitle: (bucket: Date) => format(bucket, 'eee d'),
      },
    };
    const bucketOps = BUCKET_OPERATIONS[this.props.content.options.timeScale];

    function bucketize<T extends DataValid>(entry: T): T & Bucket {
      const startDate = entry.startDate;
      const endDate = entry.endDate;
      const startBucket = startDate && bucketOps.start(startDate);
      const endBucket = endDate && bucketOps.start(endDate);
      const startPosition =
        startDate &&
        startBucket &&
        differenceInSeconds(startDate, startBucket) /
          differenceInSeconds(bucketOps.end(startDate), startBucket);
      const endPosition =
        endDate &&
        endBucket &&
        differenceInSeconds(endDate, endBucket) /
          differenceInSeconds(bucketOps.end(endDate), endBucket);
      const buckets = startBucket && endBucket && bucketOps.difference(endBucket, startBucket) + 1;
      const length =
        buckets != undefined && startPosition != undefined && endPosition != undefined
          ? buckets - 1 + endPosition - startPosition
          : undefined;
      return {
        ...entry,
        startBucket,
        endBucket,
        startPosition,
        endPosition,
        buckets,
        length,
      };
    }

    const groups = groupByMap(dataValid, (entry) => this.renderField(entry.category));
    const mappedGroups = [...groups].map(([category, entries]) => {
      const minDate = min(entries.map(({ startDate }) => startDate).filter(isDefined));
      const maxDate = max(entries.map(({ endDate }) => endDate).filter(isDefined));
      return {
        id: category,
        label: category ?? 'Uncategorized',
        startDate: minDate,
        endDate: maxDate,
        ...(minDate && maxDate && bucketize({ startDate: minDate, endDate: maxDate })),
        entries: entries.map(bucketize),
      };
    });
    const categories = sortByKey(mappedGroups, 'id');
    const uncategorizedIdx = categories.findIndex((c) => !c.id && c.label === 'Uncategorized');
    if (uncategorizedIdx !== -1 && uncategorizedIdx !== categories.length - 1) {
      const uncategorized = categories.splice(uncategorizedIdx, 1);
      categories.push(...uncategorized);
    }

    const minBucket = min(categories.map(({ startBucket }) => startBucket).filter(isDefined));
    const maxBucket = max(categories.map(({ endBucket }) => endBucket).filter(isDefined));
    const columns: {
      start: Date;
      label: string;
      days: GanttSubcolumn[];
      end: Date;
    }[] = [];
    if (minBucket && maxBucket) {
      for (let bucket = minBucket; bucket <= maxBucket; bucket = bucketOps.next(bucket)) {
        const days: GanttSubcolumn[] = [];
        const end = bucketOps.end(bucket);
        for (let day = bucket; day < end; day = addDays(day, 1)) {
          days.push({ label: bucketOps.dayTitle(day), weekend: isWeekend(day) });
        }
        columns.push({
          start: bucket,
          label: bucketOps.title(bucket),
          days,
          end,
        });
      }
    }

    const nowDate = new Date();
    const nowColumnIndex = columns.findIndex(
      ({ start, end }) => nowDate >= start && nowDate <= end,
    );
    const nowColumn = nowColumnIndex < 0 ? undefined : columns[nowColumnIndex];
    const now: GanttMarker | undefined = this.hasBoundValue
      ? nowColumn && {
          column: nowColumnIndex,
          pos:
            differenceInSeconds(nowDate, nowColumn.start) /
            differenceInSeconds(bucketOps.end(nowDate), nowColumn.start),
        }
      : PLACEHOLDER_NOW_MARKER;

    const tooltip = (entry: DataValid): string | undefined =>
      entry.startDate &&
      entry.endDate &&
      `${format(entry.startDate, 'PP')} - ${format(entry.endDate, 'PP')} • ${differenceInDays(entry.endDate, entry.startDate)} days`;

    const cells = Array(columns.length);

    const categoryIds = new Set(categories.map(({ id }) => id));
    const entryIds = new Set(data.map(({ id }) => id));

    return {
      columns: columns.map(({ label, days }) => ({ label, days })),
      withCategories: !!this.getColumnName(this.props?.content?.fields?.category),
      categories: categories.map((category) => ({
        type: 'category',
        id: category.id,
        label: category.label,
        column: columns.findIndex(({ start }) => isEqual(start, category.startBucket)),
        start: category.startPosition,
        length: category.length,
        buckets: category.buckets,
        tooltip: tooltip(category),
        cells,
        entries: category.entries.map((entry) => ({
          type: 'entry',
          id: entry.id,
          task: this.renderField(entry.task) || String(PLACEHOLDER_DATA[0].task),
          custom$: entry.custom$,
          column: columns.findIndex(({ start }) => isEqual(start, entry.startBucket)),
          start: entry.startPosition,
          length: entry.length,
          buckets: entry.buckets,
          tooltip: tooltip(entry),
          cells,
        })),
      })),
      now,
      collapsedCategories: new Set(
        [...(this.model$$.value.collapsedCategories || [])].filter((id) => categoryIds.has(id)),
      ),
      selectedId:
        (entryIds.has(this.model$$.value.selectedId) ? this.model$$.value.selectedId : undefined) ??
        data[0]?.id,
    };
  }

  private renderField(field: unknown | undefined): string | undefined {
    if (field == undefined) {
      return undefined;
    }
    const renderItem = (item: unknown): string =>
      isDatasetLinkValue(item) ? getDatasetLinkDisplay(item) : String(item);
    return Array.isArray(field) ? field.map(renderItem).join(', ') : renderItem(field);
  }

  private updateCollapse(): void {
    this.model$$.value.categories.forEach(({ id }) => {
      this.collapseCategory(id, !!this.model$$.value.collapsedCategories.has(id));
    });
  }

  private updateOutput(data: unknown | undefined): void {
    if (this.outputEmitTracker.shouldEmit(data)) {
      return;
    }

    this.output.emit({
      eventType: 'row-select',
      data,
      schema: this.getEventSchema('row-select'),
      isInitialData: false,
    });
  }

  private updateStyles(): void {
    const { content, style } = this.props || {};
    const { title } = content || {};
    const {
      fontSize,
      fontFamily,
      lineHeight,
      general,
      table,
      headers,
      title: titleStyle,
      container,
    } = style || {};
    if (!table || !headers || !titleStyle || !container) {
      return;
    }

    this.scrollHeight = `${(this.scrollContainer?.nativeElement?.offsetHeight ?? 0) - (this.scrollContainer?.nativeElement?.clientHeight ?? 0)}px`;
    this.headerWidth = headers.width;
    this.fontSize = fontSize as string;
    this.fontFamily = fontFamily as string;
    this.lineHeight = lineHeight as string;
    this.titleColor = titleStyle.text.color1;
    this.headerColor = headers.text.color1;
    this.headerBackground = headers.background.color1;
    this.accentColor = headers.accent.color1;
    this.hoverColor = setAlphaToHexColor(headers.accent.color1, 5);
    this.selectColor = setAlphaToHexColor(headers.accent.color1, 10);
    this.cellColor = table.text.color1;
    this.cellBackground = table.background.color1;
    this.borderColor = table.lines.selectorType === 'none' ? 'transparent' : table.lines.color1;
    this.borderRadius = table.radius;
    const corners = formatBorderRadiusCorners(table.radius);
    this.borderRadiusBl = corners.bottomLeft;
    this.borderRadiusBr = corners.bottomRight;
    this.borderless = table.hideColumnsBorder;
    this.wrapperStyles = {
      ...makeContainerVisualEffectStyles(general?.visualEffect),
      ...makeBorderStyles(container.outline, container.outlineWidth),
    };
    this.internalPadding = general?.offset.padding;

    this.labelEnabled = title?.enabled ?? true;
    this.labelText = title?.label || '';
    this.icon = title?.icon || '';
  }

  private getDataSource(): DataBindingSourceInfo | undefined {
    return arePropsWithData(this.props)
      ? this.props.data.find((data) => data?.key === 'value')?.dataSource
      : undefined;
  }
}

function getRowActions(
  content: (WithRowActions & RowClickAction) | undefined,
  includeAllActions = false,
): PropertiesConfigInputContainerItemActionModel[] {
  const actions = content?.rowActions
    ? [...content.rowActions.filter((a) => a.label || a.icon)]
    : [];

  if (includeAllActions && content?.itemAdd?.enabled) {
    actions.push({
      id: ITEMS_CONTAINER_ACTION_ADD,
      label: content?.itemAdd.label,
      icon: 'icon_add_16',
      styleSheetTarget: content.itemAdd.styleSheetTarget,
      styleSheetId: content.itemAdd.styleSheetId,
    });
  }
  if (content?.itemView?.enabled) {
    actions.push({
      id: ITEMS_CONTAINER_ACTION_VIEW,
      label: 'View',
      icon: 'icon_preview_16',
      styleSheetTarget: content.itemView.styleSheetTarget,
      styleSheetId: content.itemView.styleSheetId,
    });
  }
  if (content?.itemEdit?.enabled) {
    actions.push({
      id: ITEMS_CONTAINER_ACTION_EDIT,
      label: 'Edit',
      icon: 'icon_edit_24',
      styleSheetTarget: content.itemEdit.styleSheetTarget,
      styleSheetId: content.itemEdit.styleSheetId,
    });
  }
  if (content?.itemDelete?.enabled) {
    actions.push({
      id: ITEMS_CONTAINER_ACTION_DELETE,
      label: 'Delete',
      icon: 'icon_trash_16',
      styleSheetTarget: content.itemDelete.styleSheetTarget,
      styleSheetId: content.itemDelete.styleSheetId,
    });
  }
  if (includeAllActions && content?.rowClick?.enabled && content.rowClick.action) {
    actions.push({ id: ITEMS_CONTAINER_ACTION_ROW_CLICK, label: 'On row click' });
  }

  return actions;
}
